/**
 * SampleContainerPhCheckDialog — modal popup for pH Check on the Sample
 * Registration detail page, matching XAF's `btnPH_Execute` behavior
 * (SampleContainerpHCheckViewController.cs line 529).
 *
 * XAF opens a `SampleBottleAllocation_ListView_pHCheck_Samplecheckin`
 * modal scoped to the current job, showing SampleID + ContainerID +
 * pHCheck (editable, batch-edit) — no OK/Cancel buttons.  Save is
 * triggered by the grid's batch-update event.
 *
 * This dialog reproduces that:
 *  · Fetches `GET /api/v1/sample-container-ph-check/by-job?jobId=...`
 *  · Shows an AppDialog with an AppDataGrid (SampleID, ContainerID, pHCheck)
 *  · pHCheck is inline-editable (batch-edit pattern, same as the standalone page)
 *  · Save sends PUT per edited row, then reloads
 *  · Right-click "Copy To All Selected" copies pH to every selected row
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow } from '../../../components/confirm';
import { useNavActions } from '../../../hooks/useNavActions';
import { LoaderSymbol } from '../../../components/loading';

interface PhRow {
  oid:         string;
  jobId:       string;
  sampleId:    string;
  division:    string;
  containerId: string;
  phCheck:     string | null;
  recordedPh:  string | null;   // free text; preserved on save (this popup edits pHCheck only)
  adjustedPh:  string | null;
  statusName:  string;
  statusId:    string;
}

interface Props {
  open: boolean;
  jobId: string;
  onClose: () => void;
}

export function SampleContainerPhCheckDialog({ open, jobId, onClose }: Props) {
  const notify  = useNotify();
  const runSave = useSaveFlow();
  const canWrite = useNavActions('sampphcheck').write;

  const [rows,    setRows]    = useState<PhRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [phEdits, setPhEdits] = useState<Record<string, string>>({});
  const [saving,  setSaving]  = useState(false);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await api.get('/api/v1/sample-container-ph-check/by-job', {
        params: { jobId },
        silentLoading: true,
      } as never);
      setRows((res.data as { data: PhRow[] }).data ?? []);
      setPhEdits({});
      setSelected(new Set());
    } catch {
      notify.error('Failed to load pH Check data.');
    } finally {
      setLoading(false);
    }
  }, [jobId, notify]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  const stagePhEdit = useCallback((row: PhRow, newValue: string) => {
    const trimmed = newValue.trim();
    setPhEdits(prev => {
      const next = { ...prev };
      if (trimmed === (row.phCheck ?? '')) delete next[row.oid];
      else next[row.oid] = trimmed;
      return next;
    });
  }, []);

  const dirtyCount = Object.keys(phEdits).length;

  const cancelEdits = useCallback(() => {
    setPhEdits({});
    setSelected(new Set());
  }, []);

  const save = useCallback(() => {
    if (dirtyCount === 0) { notify.info('No changes to save.'); return; }
    setSaving(true);
    runSave({
      formName: 'Sample Container pH Check',
      auditAction: 'edit',
      save: async () => {
        for (const [oid, value] of Object.entries(phEdits)) {
          // This popup edits pHCheck only — pass the row's current Recorded/Adjusted
          // pH back so the writer (which sets every column) preserves them.
          const row = rows.find(r => r.oid === oid);
          await api.put(`/api/v1/sample-container-ph-check/${oid}`, {
            phCheck:    value || null,
            recordedPh: row?.recordedPh ?? null,
            adjustedPh: row?.adjustedPh ?? null,
          });
        }
        await load();
      },
      successMessage: `Updated ${dirtyCount} container(s).`,
    }).finally(() => setSaving(false));
  }, [dirtyCount, phEdits, rows, notify, runSave, load]);

  // "Copy To All Selected" — copies the last edited or first selected row's
  // pH value to every selected row (XAF's "Copy To All Cell" context menu).
  const copyToAllSelected = useCallback(() => {
    if (selected.size === 0) { notify.info('Select rows first.'); return; }
    const selArr = Array.from(selected);
    const sourceOid = selArr[0] as string;
    const sourceRow = rows.find(r => r.oid === sourceOid);
    if (!sourceRow) return;
    const sourceVal = phEdits[sourceOid] ?? (sourceRow.phCheck ?? '');
    if (!sourceVal.trim()) { notify.info('Source pH value is empty.'); return; }

    setPhEdits(prev => {
      const next = { ...prev };
      for (const oid of selArr) {
        const row = rows.find(r => r.oid === (oid as string));
        if (row && row.oid !== sourceOid) {
          if (sourceVal.trim() === (row.phCheck ?? '')) delete next[row.oid];
          else next[row.oid] = sourceVal.trim();
        }
      }
      return next;
    });
    notify.success(`Copied pH ${sourceVal} to ${selArr.length - 1} row(s).`);
  }, [selected, rows, phEdits, notify]);

  const cols: AppColumn<PhRow>[] = useMemo(() => [
    { field: 'sampleId',    header: 'Sample ID',    width: 140 },
    { field: 'containerId', header: 'Container ID', width: 120 },
    { field: 'phCheck',     header: 'pHCheck',      width: 110,
      value: r => phEdits[r.oid] ?? (r.phCheck ?? ''),
      text:  r => phEdits[r.oid] ?? (r.phCheck ?? ''),
      edit: { type: 'text', trigger: 'click', disabled: () => !canWrite } },
  ], [phEdits, canWrite]);

  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      {canWrite && (
        <>
          <button className="ca-btn" onClick={copyToAllSelected}
            disabled={saving || selected.size < 2}
            title="Copy the first selected row's pH to all other selected rows">
            <span className="tb-i">📋</span> Copy To All Selected
          </button>
          <button className="ca-btn ca-btn-solid" onClick={save}
            disabled={saving || dirtyCount === 0}>
            {saving
              ? <><LoaderSymbol size={13} /> Saving…</>
              : <><span className="tb-i">💾</span> Save</>}
          </button>
          <button className="ca-btn" onClick={cancelEdits}
            disabled={saving || dirtyCount === 0}>
            <span className="tb-i">✕</span> Cancel
          </button>
        </>
      )}
      <button className="ca-btn" onClick={onClose}>Close</button>
    </div>
  );

  if (!open) return null;

  return (
    <AppDialog
      open={open}
      title={`Sample Container pH Check — ${jobId}`}
      icon="ti-flask"
      width={520}
      onClose={onClose}
      footer={footer}
      maximizable
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <LoaderSymbol size={32} />
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--ai-muted)' }}>
          No container pH check records for this job.
        </div>
      ) : (
        <div style={{ minHeight: 200 }}>
          <AppDataGrid
            rows={rows}
            columns={cols}
            getRowId={(r) => r.oid}
            defaultPageSize={50}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            onCellCommit={(row, col, val) => {
              if (col.field === 'phCheck') stagePhEdit(row, val);
            }}
          />
          {dirtyCount > 0 && (
            <div style={{ fontSize: 12, color: 'var(--ai-muted)', marginTop: 4 }}>
              {dirtyCount} unsaved change{dirtyCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </AppDialog>
  );
}
